import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, CheckCircle2, CircleSlash, Clock, Loader2, PackageCheck, Play, StickyNote, Tag } from "lucide-react";
import { cn } from "@/lib/utils";
import { hasPermission } from "@/lib/types";
import type {
  OrderDetail,
  OrderProductionListItem,
  Product,
  ProductionItemStatus,
} from "@/lib/types";
import { fetchAdminOrder, transitionOrder, updateProductionListItem } from "@/lib/ordersApi";
import { fetchAdminProduct } from "@/lib/adminProductsApi";
import { useApp } from "@/store/useApp";
import { getApiErrorMessage } from "@/lib/api";
import { formatDeliveryLabel, orderTitle } from "@/lib/orderUtils";
import { CLIENT_TYPE_LABELS } from "@/lib/clientTypeLabels";
import { ProductionDecisionModal } from "@/components/admin/ProductionDecisionModal";
import { AdvanceConfirmDialog } from "@/components/admin/AdvanceConfirmDialog";
import { LabelDialog } from "@/components/admin/LabelDialog";

const PROCESSED_STATUSES: ProductionItemStatus[] = ["PRONTO", "PARCIAL", "SEM_MERCADORIA", "CANCELADO"];

function isDecided(item: OrderProductionListItem): boolean {
  return PROCESSED_STATUSES.includes(item.status);
}

interface Props {
  orderId: string | null;
  onClose: () => void;
  onOrderChanged?: (order: OrderDetail) => void;
}

export function ProductionOrderDialog({ orderId, onClose, onOrderChanged }: Props) {
  // Ações por PERMISSÃO (o backend revalida): decidir item processado exige PRODUCAO.AVANCAR; item in
  // natura aceita PRODUCAO.IN_NATURA ou AVANCAR; avançar/retornar etapa exigem AVANCAR/RETORNAR_ETAPA.
  const account = useApp((s) => s.account);
  const canAdvanceProd = hasPermission(account, "PEDIDOS.PRODUCAO.AVANCAR");
  const canInNatura = hasPermission(account, "PEDIDOS.PRODUCAO.IN_NATURA");
  const canReturnStage = hasPermission(account, "PEDIDOS.PEDIDO.RETORNAR_ETAPA");
  const canGenerateLabels = hasPermission(account, "ETIQUETAS.ETIQUETA.GERAR");
  const [order, setOrder] = useState<OrderDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [advanceOpen, setAdvanceOpen] = useState(false);
  const [backOpen, setBackOpen] = useState(false);
  const [decisionModal, setDecisionModal] = useState<{
    item: OrderProductionListItem;
    target: "PRONTO" | "PARCIAL" | "SEM_MERCADORIA";
  } | null>(null);
  const [labelProduct, setLabelProduct] = useState<Product | null>(null);
  const [labelOpen, setLabelOpen] = useState(false);

  const openLabel = async (item: OrderProductionListItem) => {
    try {
      const detail = await fetchAdminProduct(item.productId);
      setLabelProduct(detail);
      setLabelOpen(true);
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Não foi possível carregar o produto"));
    }
  };

  useEffect(() => {
    if (!orderId) {
      setOrder(null);
      return;
    }
    setLoading(true);
    fetchAdminOrder(orderId)
      .then(setOrder)
      .catch((error) => toast.error(getApiErrorMessage(error, "Não foi possível carregar o pedido")))
      .finally(() => setLoading(false));
  }, [orderId]);

  const applyStatus = async (
    item: OrderProductionListItem,
    payload: { status: ProductionItemStatus; useStock?: boolean; producedQuantity?: number; reason?: string },
    message: string,
  ) => {
    if (!order) return;
    try {
      const updated = await updateProductionListItem(order.id, {
        productId: item.productId,
        unit: item.unit,
        cut: item.cut ?? null,
        ...payload,
      });
      setOrder(updated);
      onOrderChanged?.(updated);
      toast.success(message);
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Não foi possível atualizar o item"));
    }
  };

  const markPronto = (item: OrderProductionListItem) =>
    applyStatus(item, { status: "PRONTO" }, "Item marcado como Completo");

  // "Iniciar": sinaliza que o item entrou em produção (EM_ANDAMENTO). Não é decisão final —
  // apenas informa o começo. Clicar de novo no ícone ativo volta para pendente.
  const markEmAndamento = (item: OrderProductionListItem) =>
    applyStatus(item, { status: "EM_ANDAMENTO" }, "Produção iniciada");

  // Desfazer uma decisão clicada por engano: clicar de novo no status ativo volta para pendente.
  const revertToPending = (item: OrderProductionListItem) =>
    applyStatus(item, { status: "PENDENTE" }, "Item voltou para pendente");

  const submitAdvance = async (date: string) => {
    if (!order) return;
    try {
      const updated = await transitionOrder(order.id, {
        toStatus: "EM_EXPEDICAO",
        transitionDate: date,
      });
      onOrderChanged?.(updated);
      toast.success(`${orderTitle(order)} → Expedição`);
      setAdvanceOpen(false);
      onClose();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Não foi possível avançar o pedido"));
    }
  };

  // Retorno de etapa: traz um pedido já em expedição de volta para a produção.
  const submitBackToProduction = async (date: string) => {
    if (!order) return;
    try {
      const updated = await transitionOrder(order.id, {
        toStatus: "EM_PRODUCAO",
        transitionDate: date,
      });
      onOrderChanged?.(updated);
      toast.success(`${orderTitle(order)} voltou para produção`);
      setBackOpen(false);
      onClose();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Não foi possível voltar o pedido para produção"));
    }
  };

  const productionList = order?.productionList ?? [];
  const pendingCount = productionList.filter((i) => !isDecided(i)).length;
  const decidedCount = productionList.length - pendingCount;
  // Empty production list = pedido sem itens, nada pra decidir.
  const allDecided = order != null && pendingCount === 0;
  // Decisões só são editáveis enquanto o pedido está em produção (igual à expedição).
  const editable = order?.status === "EM_PRODUCAO";
  const canDecideItem = (item: OrderProductionListItem) =>
    editable && (item.isProcessed ? canAdvanceProd : (canAdvanceProd || canInNatura));

  return (
    <>
      <Dialog open={orderId != null} onOpenChange={(open) => !open && onClose()}>
        <DialogContent className="flex max-h-[88vh] w-[calc(100vw-2rem)] max-w-2xl flex-col gap-0 overflow-hidden p-0">
          <DialogHeader>
            <DialogTitle className="sr-only">Decisões de produção</DialogTitle>
            <DialogDescription className="sr-only">
              Decida cada item processado da lista de produção do pedido.
            </DialogDescription>
          </DialogHeader>

          {loading && (
            <div className="flex items-center justify-center py-16 text-sm text-muted-foreground">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Carregando pedido...
            </div>
          )}

          {!loading && order && (
            <>
              <header className="shrink-0 border-b border-border px-6 pt-6 pb-4">
                <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                  <span className="font-display text-lg font-semibold">{orderTitle(order)}</span>
                  <SubStatusBadge decided={decidedCount} total={productionList.length} />
                </div>
                <div className="mt-1 text-sm text-muted-foreground">
                  {CLIENT_TYPE_LABELS[order.clientType]} · entrega{" "}
                  <strong className="font-medium text-foreground">{formatDeliveryLabel(order)}</strong>
                </div>
                <div className="mt-3 flex items-center justify-between">
                  <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    Lista de produção
                  </h3>
                  <span className="text-xs text-muted-foreground">
                    {decidedCount}/{productionList.length} decididos
                    {pendingCount > 0 ? ` · ${pendingCount} pendentes` : ""}
                  </span>
                </div>
              </header>

              <div className="flex-1 overflow-y-auto px-6 py-4">
                {productionList.length === 0 ? (
                  <div className="rounded-lg border border-dashed border-border/60 p-6 text-center text-sm text-muted-foreground">
                    Nenhum item neste pedido.
                  </div>
                ) : (
                  <div className="overflow-hidden rounded-xl border border-border bg-surface">
                    <table className="w-full text-sm" style={{ tableLayout: "fixed" }}>
                      <colgroup>
                        <col style={{ width: "34%" }} />
                        <col style={{ width: "15%" }} />
                        <col style={{ width: "15%" }} />
                        <col style={{ width: "36%" }} />
                      </colgroup>
                      <thead className="bg-surface-elevated text-xs uppercase text-muted-foreground">
                        <tr>
                          <th className="px-3 py-2 text-left font-medium">Produto</th>
                          <th className="px-3 py-2 text-right font-medium">Pedido</th>
                          <th className="px-3 py-2 text-right font-medium">Produzido</th>
                          <th className="px-3 py-2 text-right font-medium">{editable ? "Ações" : "Status"}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {productionList.map((item) => {
                          const cancelled = item.status === "CANCELADO";
                          const prod = producedDisplay(item);
                          const showCut = item.cut && item.cut.trim().toUpperCase() !== "IN NATURA";
                          return (
                            <tr
                              key={`${item.productId}:${item.unit}:${item.cut ?? ""}`}
                              className={cn("border-t border-border align-top", cancelled && "opacity-60")}
                            >
                              <td className="px-3 py-2.5">
                                <div className={cn("font-medium leading-tight", cancelled && "line-through")}>
                                  {item.productName}
                                </div>
                                {(showCut || !item.isProcessed) && (
                                  <div className="mt-1 flex flex-wrap items-center gap-1">
                                    {showCut && (
                                      <span className="rounded bg-primary/10 px-1.5 py-0.5 text-[10px] font-bold uppercase text-primary">
                                        {item.cut}
                                      </span>
                                    )}
                                    {!item.isProcessed && (
                                      <span className="rounded-full border border-lime-500/30 bg-lime-500/10 px-2 py-0.5 text-[10px] font-medium text-lime-700">
                                        IN NATURA
                                      </span>
                                    )}
                                  </div>
                                )}
                                {item.reason && (
                                  <div className="mt-1 text-[10px] text-muted-foreground">{item.reason}</div>
                                )}
                                {item.observation && (
                                  <div className="mt-1 flex items-start gap-1 rounded bg-amber-50 px-1.5 py-1 text-xs text-amber-700">
                                    <StickyNote className="mt-0.5 h-3 w-3 shrink-0" />
                                    <span className="whitespace-pre-wrap font-medium">{item.observation}</span>
                                  </div>
                                )}
                              </td>

                              <td className="px-3 py-2.5">
                                <QtyNum value={fmtQty(item.requestedQuantity)} unit={item.unit} />
                                {item.productionQuantity < item.requestedQuantity && (
                                  <div className="text-right text-[10px] font-medium uppercase tracking-wide text-warning">
                                    {item.productionQuantity > 0
                                      ? `comprado ${fmtQty(item.productionQuantity)}`
                                      : "não comprado"}
                                  </div>
                                )}
                              </td>

                              <td className="px-3 py-2.5">
                                <QtyNum value={prod.value} unit={item.unit} tone={prod.tone} strike={cancelled} />
                              </td>

                              <td className="px-3 py-2.5 text-right">
                                {canDecideItem(item) ? (
                                  <div className="flex justify-end gap-1">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      title="Iniciar produção (clique de novo p/ voltar a pendente)"
                                      aria-label="Iniciar produção"
                                      onClick={() =>
                                        void (item.status === "EM_ANDAMENTO"
                                          ? revertToPending(item)
                                          : markEmAndamento(item))
                                      }
                                      className={cn(
                                        "h-8 w-8 p-0 text-sky-700 hover:bg-sky-500/10",
                                        item.status === "EM_ANDAMENTO" && "bg-sky-600 text-white hover:bg-sky-600 hover:text-white",
                                      )}
                                    >
                                      <Play className="h-4 w-4" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      title="Completo (clique de novo p/ voltar a pendente)"
                                      aria-label="Marcar como completo"
                                      onClick={() => void (item.status === "PRONTO" ? revertToPending(item) : markPronto(item))}
                                      className={cn(
                                        "h-8 w-8 p-0 text-emerald-700 hover:bg-emerald-500/10",
                                        item.status === "PRONTO" && "bg-emerald-600 text-white hover:bg-emerald-600 hover:text-white",
                                      )}
                                    >
                                      <CheckCircle2 className="h-4 w-4" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      title="Parcial"
                                      aria-label="Marcar como parcial"
                                      onClick={() =>
                                        item.status === "PARCIAL"
                                          ? void revertToPending(item)
                                          : setDecisionModal({ item, target: "PARCIAL" })
                                      }
                                      className={cn(
                                        "h-8 w-8 p-0 text-amber-700 hover:bg-amber-500/10",
                                        item.status === "PARCIAL" && "bg-amber-500 text-white hover:bg-amber-500 hover:text-white",
                                      )}
                                    >
                                      <PackageCheck className="h-4 w-4" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      title="Em falta / não produzido"
                                      aria-label="Marcar em falta"
                                      onClick={() =>
                                        item.status === "SEM_MERCADORIA"
                                          ? void revertToPending(item)
                                          : setDecisionModal({ item, target: "SEM_MERCADORIA" })
                                      }
                                      className={cn(
                                        "h-8 w-8 p-0 text-destructive hover:bg-destructive/10",
                                        item.status === "SEM_MERCADORIA" && "bg-destructive text-destructive-foreground hover:bg-destructive",
                                      )}
                                    >
                                      <CircleSlash className="h-4 w-4" />
                                    </Button>
                                    {canGenerateLabels && (
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        title="Etiqueta"
                                        aria-label="Etiqueta"
                                        onClick={() => void openLabel(item)}
                                        className="h-8 w-8 p-0 text-muted-foreground hover:bg-muted"
                                      >
                                        <Tag className="h-4 w-4" />
                                      </Button>
                                    )}
                                  </div>
                                ) : (
                                  <div className="flex justify-end">
                                    <ProductionStatusBadge status={item.status} />
                                  </div>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <footer className="flex shrink-0 items-center gap-2 border-t border-border bg-surface-elevated/40 px-6 py-3">
                {order.status === "EM_EXPEDICAO" && canReturnStage && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-transparent bg-warning text-white hover:bg-warning/90 hover:text-white"
                    onClick={() => setBackOpen(true)}
                  >
                    <ArrowLeft className="h-4 w-4" />
                    Voltar para produção
                  </Button>
                )}
                {editable && canAdvanceProd && (
                  <Button
                    variant="hero"
                    size="sm"
                    className="ml-auto"
                    onClick={() => setAdvanceOpen(true)}
                    disabled={!allDecided}
                    title={!allDecided ? `Decida todos os itens (${pendingCount} pendentes)` : undefined}
                  >
                    <ArrowRight className="h-4 w-4" />
                    Avançar para Expedição
                    {pendingCount > 0 && ` (${pendingCount} pendentes)`}
                  </Button>
                )}
              </footer>
            </>
          )}
        </DialogContent>
      </Dialog>

      <AdvanceConfirmDialog
        open={advanceOpen}
        toStatusLabel="Expedição"
        pedidoLabel={order ? orderTitle(order) : ""}
        onCancel={() => setAdvanceOpen(false)}
        onConfirm={submitAdvance}
      />

      <AdvanceConfirmDialog
        open={backOpen}
        actionLabel="Voltar para"
        toStatusLabel="Produção"
        pedidoLabel={order ? orderTitle(order) : ""}
        onCancel={() => setBackOpen(false)}
        onConfirm={submitBackToProduction}
      />

      {decisionModal && (
        <ProductionDecisionModal
          item={decisionModal.item}
          target={decisionModal.target}
          onCancel={() => setDecisionModal(null)}
          onSubmit={async ({ useStock, quantity, reason }) => {
            await applyStatus(
              decisionModal.item,
              {
                status: decisionModal.target,
                useStock,
                producedQuantity: quantity,
                reason,
              },
              decisionModal.target === "PARCIAL"
                ? "Produção parcial registrada"
                : "Item marcado como em falta",
            );
            setDecisionModal(null);
          }}
        />
      )}

      <LabelDialog open={labelOpen} product={labelProduct} onClose={() => setLabelOpen(false)} />
    </>
  );
}

/** Quantidade legível: remove zeros à direita; "—" quando ausente. */
function fmtQty(n?: number | string | null): string {
  if (n == null) return "—";
  return Number(n).toFixed(3).replace(/\.?0+$/, "");
}

type QtyTone = "muted" | "strong" | "ok" | "warn" | "bad";

const QTY_TONE: Record<QtyTone, string> = {
  muted: "text-muted-foreground",
  strong: "text-foreground",
  ok: "text-emerald-700",
  warn: "text-amber-700",
  bad: "text-destructive",
};

function QtyNum({
  value,
  unit,
  tone = "strong",
  strike = false,
}: {
  value: string;
  unit?: string;
  tone?: QtyTone;
  strike?: boolean;
}) {
  return (
    <div className="text-right leading-tight">
      <span className={cn("text-base font-medium tabular-nums", QTY_TONE[tone], strike && "line-through")}>{value}</span>
      {value !== "—" && unit && <span className="ml-0.5 text-[11px] text-muted-foreground">{unit}</span>}
    </div>
  );
}

/** Valor/cor da coluna Produzido a partir da decisão de produção. */
function producedDisplay(item: OrderProductionListItem): { value: string; tone: QtyTone } {
  switch (item.status) {
    case "PRONTO":
      // Completo = produziu tudo que estava previsto para produção.
      return { value: fmtQty(item.productionQuantity), tone: "ok" };
    case "PARCIAL":
      return { value: fmtQty(item.producedQuantity), tone: "warn" };
    case "SEM_MERCADORIA":
      return { value: fmtQty(item.producedQuantity ?? 0), tone: "bad" };
    case "CANCELADO":
      return { value: fmtQty(item.producedQuantity ?? 0), tone: "muted" };
    default:
      // PENDENTE / EM_ANDAMENTO — ainda não produzido.
      return { value: "—", tone: "muted" };
  }
}

function SubStatusBadge({ decided, total }: { decided: number; total: number }) {
  if (total === 0) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-secondary px-2 py-0.5 text-xs font-medium text-muted-foreground">
        Sem itens
      </span>
    );
  }
  if (decided === 0) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-secondary px-2 py-0.5 text-xs font-medium text-muted-foreground">
        <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground" /> Para produzir
      </span>
    );
  }
  if (decided === total) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-success/10 px-2 py-0.5 text-xs font-medium text-success-foreground">
        <CheckCircle2 className="h-3 w-3" /> Produzido
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-warning/10 px-2 py-0.5 text-xs font-medium text-warning-foreground">
      <span className="h-1.5 w-1.5 rounded-full bg-warning" /> Produzindo
    </span>
  );
}

function ProductionStatusBadge({ status }: { status: ProductionItemStatus }) {
  const variants: Record<ProductionItemStatus, { className: string; label: string; Icon?: typeof CheckCircle2 }> = {
    PENDENTE:       { className: "bg-muted text-muted-foreground",            label: "Pendente",    Icon: Clock },
    EM_ANDAMENTO:   { className: "bg-sky-500/10 text-sky-700",                label: "Em andamento", Icon: Play },
    PRONTO:         { className: "bg-emerald-500/10 text-emerald-700",        label: "Completo",    Icon: CheckCircle2 },
    PARCIAL:        { className: "bg-amber-500/10 text-amber-700",            label: "Parcial" },
    SEM_MERCADORIA: { className: "bg-destructive/10 text-destructive",        label: "Em falta",    Icon: CircleSlash },
    CANCELADO:      { className: "bg-muted text-muted-foreground line-through", label: "Cancelado" },
  };
  const { className, label, Icon } = variants[status];
  return (
    <span className={cn("inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium", className)}>
      {Icon && <Icon className="h-3 w-3" />}
      {label}
    </span>
  );
}
